import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { favoriteService } from '../../services/favoriteService';
import Header from '../../components/ui/Header';
import EventCard from '../event-discovery/components/EventCard';
import SpotCard from '../spot-guide/components/SpotCard';
import ProductCard from '../official-store/components/ProductCard';
import ListingCard from '../user-marketplace/components/ListingCard';

// Loading Skeleton
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 animate-pulse">
    <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
  </div>
);

export default function MyFavorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all, event, spot, product, marketplace
  const navigate = useNavigate();

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const data = await favoriteService?.getMyFavorites();
      console.log('Fetched Favorites:', data); // Debugging
      setFavorites(data || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on active tab
  const filteredItems = activeTab === 'all'
    ? favorites
    : favorites?.filter(item => item?.itemType === activeTab);

  // Helper to render correct card based on type
  const renderCard = (item) => {
    switch (item?.itemType) {
      case 'event':
        return <EventCard key={item?.id} event={item} />;
      case 'spot':
        return <SpotCard key={item?.id} spot={item} />;
      case 'product':
        return <ProductCard 
          key={item?.id} 
          product={item}
          onAddToCart={() => {}}
          onQuickView={() => {}}
          onToggleWishlist={() => {}}
          isInWishlist={true}
        />;
      case 'marketplace':
        return <ListingCard 
          key={item?.id} 
          listing={item}
          onContact={() => {}}
          onWishlist={() => {}}
          isWishlisted={true}
          isOwner={false}
          onMarkAsSold={() => {}}
          onDelete={() => {}}
        />;
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'all', label: 'Tümü' },
    { id: 'event', label: 'Etkinlikler' },
    { id: 'spot', label: 'Spotlar' },
    { id: 'product', label: 'Mağaza' },
    { id: 'marketplace', label: 'İkinci El' },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Favorilerim</h1>
            <p className="text-gray-500 mt-2">Kaydettiğiniz tüm içerikler burada.</p>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-2 mb-8 pb-2 scrollbar-hide">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Content Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3]?.map(n => <SkeletonCard key={n} />)}
            </div>
          ) : filteredItems?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredItems?.map(item => renderCard(item))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-8 h-8 text-gray-400"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Henüz favori yok</h3>
              <p className="text-gray-500 mt-1 mb-6">
                Beğendiğiniz içerikleri kalp ikonuna basarak buraya ekleyebilirsiniz.
              </p>
              <button
                onClick={() => navigate('/event-discovery')}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Keşfetmeye Başla
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}